<template>
  <div class="memory-management">
    <div class="memory-header">
      <div class="header-left">
        <h2 class="memory-title">Memory</h2>
        <p class="memory-subtitle">
          {{ companyInformation?.product_name || 'CamGPT' }} automatically remembers important facts from your conversations to provide more personalized responses.
        </p>
      </div>
      <button class="close-btn" @click="$emit('close')" title="Close">
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="18" y1="6" x2="6" y2="18"></line>
          <line x1="6" y1="6" x2="18" y2="18"></line>
        </svg>
      </button>
    </div>

    <div v-if="loading" class="memory-loading">
      <div class="spinner"></div>
      <span>Loading memories...</span>
    </div>

    <div v-else-if="error" class="memory-error">
      <p>{{ error }}</p>
      <button class="retry-btn" @click="loadMemories">Retry</button>
    </div>

    <div v-else-if="memories.length === 0" class="memory-empty">
      <div class="empty-icon">
        <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" opacity="0.4">
          <path d="M9 18h6"></path>
          <path d="M10 22h4"></path>
          <path d="M12 2a7 7 0 0 0-4 12.7V17h8v-2.3A7 7 0 0 0 12 2z"></path>
        </svg>
      </div>
      <h3>No memories yet</h3>
      <p>As you chat, {{ companyInformation?.product_name || 'CamGPT' }} will automatically remember important facts about you and your preferences to provide better, more personalized responses.</p>
    </div>

    <div v-else class="memory-list">
      <div class="memory-count">{{ memories.length }} {{ memories.length === 1 ? 'memory' : 'memories' }}</div>
      <div
        v-for="memory in memories"
        :key="memory.id"
        class="memory-item"
      >
        <div class="memory-content">
          <p class="memory-text">{{ memory.content }}</p>
          <span class="memory-date">{{ formatDate(memory.created_at) }}</span>
        </div>
        <button
          class="delete-btn"
          @click="deleteMemory(memory)"
          :disabled="deletingId === memory.id"
          title="Delete memory"
        >
          <span v-if="deletingId === memory.id" class="delete-spinner"></span>
          <svg v-else width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="3 6 5 6 21 6"></polyline>
            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
          </svg>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/axios-config';

export default {
  name: 'MemoryManagement',
  props: {
    companyInformation: Object,
  },
  data() {
    return {
      memories: [],
      loading: true,
      error: null,
      deletingId: null,
    };
  },
  mounted() {
    this.loadMemories();
  },
  methods: {
    async loadMemories() {
      this.loading = true;
      this.error = null;
      try {
        const response = await api.get('/api/memories/');
        this.memories = response.data;
      } catch (err) {
        console.error('Failed to load memories:', err);
        this.error = 'Failed to load memories. Please try again.';
      } finally {
        this.loading = false;
      }
    },
    async deleteMemory(memory) {
      this.deletingId = memory.id;
      try {
        // Ensure we have a fresh CSRF token
        await api.get('/api/get-csrf-token');
        // Get token from cookie (set by Django response)
        const csrfToken = document.cookie
          .split('; ')
          .find(row => row.startsWith('csrftoken='))
          ?.split('=')[1];
        await api.delete(`/api/memories/${memory.id}/`, {
          headers: { 'X-CSRFToken': csrfToken },
        });
        this.memories = this.memories.filter(m => m.id !== memory.id);
      } catch (err) {
        console.error('Failed to delete memory:', err);
        alert('Failed to delete memory. Please try again.');
      } finally {
        this.deletingId = null;
      }
    },
    formatDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now - date;
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

      if (diffDays === 0) return 'Today';
      if (diffDays === 1) return 'Yesterday';
      if (diffDays < 7) return `${diffDays} ${diffDays === 1 ? 'day' : 'days'} ago`;
      const weeks = Math.floor(diffDays / 7);
      if (diffDays < 30) return `${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago`;

      return date.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
      });
    },
  },
};
</script>

<style scoped>
.memory-management {
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 700px;
  margin: 0 auto;
  padding: 32px 24px;
  box-sizing: border-box;
  height: 100%;
  overflow-y: auto;
  font-family: Inter, Helvetica, sans-serif;
}

.memory-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 24px;
}

.header-left {
  flex: 1;
}

.memory-title {
  margin: 0 0 8px 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--text-primary, #1C1C1C);
}

.memory-subtitle {
  margin: 0;
  font-size: 0.9rem;
  color: var(--text-secondary, #666);
  line-height: 1.5;
}

.close-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 4px;
  border-radius: 6px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--text-tertiary, #999);
  transition: background-color 0.2s, color 0.2s;
}

.close-btn:hover {
  background-color: var(--hover-bg, #e4e4e4);
  color: var(--text-primary, #1C1C1C);
}

/* Loading */
.memory-loading {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 0;
  gap: 16px;
  color: var(--text-secondary, #666);
}

.spinner {
  width: 32px;
  height: 32px;
  border: 3px solid var(--border-color, #ddd);
  border-top-color: var(--accent-primary, #8ECAE6);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Error */
.memory-error {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 0;
  gap: 12px;
  color: #e53e3e;
}

.retry-btn {
  padding: 8px 20px;
  border: 1px solid #e53e3e;
  border-radius: 8px;
  background: none;
  color: #e53e3e;
  cursor: pointer;
  font-size: 0.88rem;
  transition: background-color 0.2s;
}

.retry-btn:hover {
  background: rgba(229, 62, 62, 0.08);
}

/* Empty State */
.memory-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 64px 24px;
  text-align: center;
}

.empty-icon {
  margin-bottom: 16px;
  color: var(--text-tertiary, #999);
}

.memory-empty h3 {
  margin: 0 0 12px 0;
  font-size: 1.2rem;
  font-weight: 600;
  color: var(--text-primary, #1C1C1C);
}

.memory-empty p {
  margin: 0;
  font-size: 0.9rem;
  color: var(--text-secondary, #666);
  line-height: 1.6;
  max-width: 400px;
}

/* Memory List */
.memory-count {
  font-size: 0.82rem;
  color: var(--text-secondary, #666);
  margin-bottom: 12px;
  font-weight: 500;
  display: inline-block;
  padding: 4px 10px;
  background: var(--bg-secondary, #fafafa);
  border-radius: 12px;
  border: 1px solid var(--border-secondary, #e8e8e8);
}

.memory-list {
  display: flex;
  flex-direction: column;
}

.memory-item {
  display: flex;
  align-items: flex-start;
  padding: 16px;
  border: 1px solid var(--border-color, #e8e8e8);
  border-radius: 10px;
  margin-bottom: 8px;
  background: var(--bg-secondary, #fafafa);
  transition: border-color 0.2s, box-shadow 0.2s, transform 0.2s;
}

.memory-item:hover {
  border-color: var(--accent-primary, #2563eb);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
  transform: translateY(-1px);
}

.memory-content {
  flex: 1;
  min-width: 0;
}

.memory-text {
  margin: 0 0 6px 0;
  font-size: 0.92rem;
  line-height: 1.5;
  color: var(--text-primary, #1C1C1C);
}

.memory-date {
  font-size: 0.78rem;
  color: var(--text-tertiary, #999);
}

.delete-btn {
  background: none;
  border: none;
  cursor: pointer;
  padding: 6px;
  border-radius: 6px;
  margin-left: 12px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  color: var(--text-tertiary, #999);
  transition: opacity 0.2s, background-color 0.2s, color 0.2s;
}

.memory-item:hover .delete-btn {
  opacity: 1;
}

.delete-btn:hover {
  background: rgba(220, 38, 38, 0.08);
  color: #dc2626;
}

.delete-btn:disabled {
  cursor: not-allowed;
  opacity: 0.3;
}

.delete-spinner {
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid #ddd;
  border-top-color: #e53e3e;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}
</style>
